import {
  GET_USERS_BY_DISTRICT_PAGINATED,
  GET_LATEST_QUALIFICATIONS_BY_USER_IDS,
  GET_USERS_SUMMARY_LIGHT,
  GET_USERS_BY_INSTITUTE_AND_DISTRICTS_PAGINATED,
  GET_USERS_BY_INSTITUTE_PAGINATED,
  GET_USERS_BY_IDS,
} from "GraphQl/Queries/User";
import { GET_SCORM_QUIZ_ATTEMPT_WITH_FILTER } from "GraphQl/Queries/Courses";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { asdmCourseReportConfig } from "api/Consts";
import { Form } from "skillstrainer-resource-library";
import Container from "components/Container/Container";
import ASDMStatsFormBuilder from "formBuilders/ASDMStatsFormBuilder";
import MultiSelectDropdown from "components/InputGroup/MultiselectDropdown";

import { useLazyQuery, useApolloClient } from "@apollo/client";
import _ from "lodash";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";

const digitalLitracyGraphData = [
  {
    question: "Have you ever used a computer?",
    question_id: "Scene1_QuestionDraw11_Slide9_FreeFormPickOne_0_0",
  },
  {
    question: "Do you have an email?",
    question_id: "Scene1_QuestionDraw11_Slide10_FreeFormPickOne_0_0",
  },
  {
    question: "Do you have your own mobile?",
    question_id: "Scene1_QuestionDraw11_Slide11_FreeFormPickOne_0_0",
  },
  {
    question: "Do you know how to use Microsoft Word?",
    question_id: "Scene1_QuestionDraw11_Slide12_FreeFormPickOne_0_0",
  },
  {
    question:
      "Do you use Social Media platforms (Facebook, Instagram, YouTube, etc.)?",
    question_id: "Scene1_QuestionDraw11_Slide13_FreeFormPickOne_0_0",
  },
  {
    question: "Do you use Online Banking / Digital Fund Transfer?",
    question_id: "Scene1_QuestionDraw11_Slide14_FreeFormPickOne_0_0",
  },
  {
    question: "Do you Shop online?",
    question_id: "Scene1_QuestionDraw11_Slide15_FreeFormPickOne_0_0",
  },
  {
    question: "Is 'Google' used to search anything online?",
    question_id: "Scene1_QuestionDraw11_Slide16_FreeFormPickOne_0_0",
  },
];
const createDigitalQuestionTemplate = () =>
  digitalLitracyGraphData.map((item, index) => ({
    ...item,
    question_index: index + 1,
    counts: { Yes: 0, No: 0 },
  }));

const SCORE_WORD_ASSOCIATION = {
  0: "Unlikely",
  1: "Unlikely",
  2: "Unlikely",
  3: "Likely",
  4: "Likely",
  5: "Preferred",
  6: "Preferred",
  7: "Most Preferred",
  8: "Most Preferred",
};

const OTHER_GENDER_LABEL = "Others";
const NO_CLASS_LABEL = "No Class Data";
const NO_INSTITUTE_LABEL = "NO INSTITUTE";
const NO_DISTRICT_LABEL = "NO DISTRICT";

const INTERPRETATION_LABELS = [
  "Unlikely",
  "Likely",
  "Preferred",
  "Most Preferred",
];

const CAREER_GROUPS = [
  { key: "g1", label: "Career Group 1", wordKey: "g1_word" },
  { key: "g2", label: "Career Group 2", wordKey: "g2_word" },
  { key: "g3", label: "Career Group 3", wordKey: "g3_word" },
  { key: "g4", label: "Career Group 4", wordKey: "g4_word" },
];

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: OTHER_GENDER_LABEL, label: OTHER_GENDER_LABEL },
];

const createCareerGroupTemplate = () =>
  CAREER_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    counts: INTERPRETATION_LABELS.reduce((acc, label) => {
      acc[label] = 0;
      return acc;
    }, {}),
  }));

const chunkArray = (items, chunkSize) => {
  const result = [];
  for (let i = 0; i < items.length; i += chunkSize)
    result.push(items.slice(i, i + chunkSize));
  return result;
};

const getLatestAttemptResponses = (attempts = []) => {
  if (!attempts.length) return [];

  const sorted = [...attempts].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const latestAttemptId = sorted[0]?.attempt_id;
  if (!latestAttemptId) return [];

  const latestAttemptResponses = sorted.filter(
    (r) => r.attempt_id === latestAttemptId,
  );

  return Object.values(
    _.groupBy(latestAttemptResponses, (r) => r.slide_id || r.question_id),
  ).map((group) => _.maxBy(group, (g) => new Date(g.created_at).getTime()));
};

const calculateScormScores = (attempts = []) => {
  if (!attempts.length) return {};

  const sorted = [...attempts].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  const latestAttemptId = sorted[0].attempt_id;
  const latestAttemptResponses = sorted.filter(
    (r) => r.attempt_id === latestAttemptId,
  );

  const normalizeBySlide = (responses) => {
    return Object.values(
      _.groupBy(responses, (r) => r.slide_id || r.question_id),
    ).map((group) => _.maxBy(group, (g) => new Date(g.created_at).getTime()));
  };

  const responses = normalizeBySlide(latestAttemptResponses);

  let digitalScore = 0;
  let g1 = 0,
    g2 = 0,
    g3 = 0,
    g4 = 0;

  responses.forEach((res) => {
    const trimmed = `${res.user_response || ""}`.trim();
    if (!trimmed) return;

    const slideSource = res.slide_id || res.question_id || "";
    const slideMatch = /Slide(\d+)/i.exec(slideSource);
    const slideNum = slideMatch ? Number(slideMatch[1]) : null;

    const firstChar = trimmed[0];
    const lastChar = trimmed[trimmed.length - 1];

    if (slideNum !== null && slideNum >= 9 && slideNum <= 16) {
      if (firstChar === "A") digitalScore += 1;
      return;
    }

    if (firstChar === "D" || lastChar === "4") g4 += 1;
    if (firstChar === "C" || lastChar === "3") g3 += 1;
    if (firstChar === "B" || lastChar === "2") g2 += 1;
    if (firstChar === "A" || lastChar === "1") g1 += 1;
  });

  g1 = Math.min(g1, 8);
  g2 = Math.min(g2, 8);
  g3 = Math.min(g3, 8);
  g4 = Math.min(g4, 8);
  digitalScore = Math.min(digitalScore, 8);

  // 7️⃣ Final output
  return {
    g1m: `${g1}/8`,
    g2m: `${g2}/8`,
    g3m: `${g3}/8`,
    g4m: `${g4}/8`,
    g1_word: SCORE_WORD_ASSOCIATION[String(g1)] || "N/A",
    g2_word: SCORE_WORD_ASSOCIATION[String(g2)] || "N/A",
    g3_word: SCORE_WORD_ASSOCIATION[String(g3)] || "N/A",
    g4_word: SCORE_WORD_ASSOCIATION[String(g4)] || "N/A",
    digital_marks: `${digitalScore}/8`,
  };
};

const normalizeGender = (gender) => {
  const value = `${gender || ""}`.trim().toLowerCase();
  if (value === "0" || value === "male") return "Male";
  if (value === "1" || value === "female") return "Female";
  return OTHER_GENDER_LABEL;
};

const normalizeClass = (value) => {
  if (value === null || value === undefined) return NO_CLASS_LABEL;
  const normalized = String(value).trim();
  return normalized ? normalized : NO_CLASS_LABEL;
};

const getInstituteLabel = (enrollment) =>
  _.get(enrollment, "batch.institute.name") || NO_INSTITUTE_LABEL;

const getDistrictLabel = (enrollment) =>
  _.get(enrollment, "batch.institute.district") || NO_DISTRICT_LABEL;

const matchesMultiSelect = (value, selected = []) => {
  if (!selected.length) return true;
  return selected.includes(value);
};

const filterSummaryRows = (
  rows,
  {
    selectedDistricts = [],
    selectedInstitutes = [],
    selectedClasses = [],
    selectedGenders = [],
    ignore = {},
  },
) => {
  const districtSet =
    !ignore.district && selectedDistricts.length
      ? new Set(selectedDistricts)
      : null;
  const instituteSet =
    !ignore.institute && selectedInstitutes.length
      ? new Set(selectedInstitutes)
      : null;
  const classSet =
    !ignore.class && selectedClasses.length ? new Set(selectedClasses) : null;
  const genderSet =
    !ignore.gender && selectedGenders.length ? new Set(selectedGenders) : null;

  if (!districtSet && !instituteSet && !classSet && !genderSet) {
    return rows;
  }

  return rows.filter((row) => {
    if (districtSet && !districtSet.has(row.district)) return false;
    if (instituteSet && !instituteSet.has(row.institute)) return false;
    if (classSet && !classSet.has(row.class_label)) return false;
    if (genderSet && !genderSet.has(row.gender_label)) return false;
    return true;
  });
};

export default function ASDMStats() {
  const client = useApolloClient();

  const [loadingPercent, setLoadingPercent] = useState(0);
  const [scormLoading, setScormLoading] = useState(false);

  const [summaryRows, setSummaryRows] = useState([]);

  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedInstitutes, setSelectedInstitutes] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedCareerGroup, setSelectedCareerGroup] = useState("");
  const [selectedInterpretation, setSelectedInterpretation] = useState("");
  const [careerGroupSummary, setCareerGroupSummary] = useState(() =>
    createCareerGroupTemplate(),
  );
  const [careerGroupLoading, setCareerGroupLoading] = useState(false);
  const [digitalQuestionSummary, setDigitalQuestionSummary] = useState(() =>
    createDigitalQuestionTemplate(),
  );
  const [digitalQuestionLoading, setDigitalQuestionLoading] = useState(false);
  const [digitalQuestionError, setDigitalQuestionError] = useState("");

  const [batchTable, setBatchTable] = useState({
    totalRows: 0,
    pageIndex: 0,
    pageSize: 25,
    rows: [],
  });

  const lastFilterRef = useRef(null);
  const careerGroupCacheRef = useRef(new Map());
  const careerGroupRequestIdRef = useRef(0);
  const digitalQuestionRequestIdRef = useRef(0);

  const [runQuerySummaryIds] = useLazyQuery(GET_USERS_SUMMARY_LIGHT, {
    fetchPolicy: "network-only",
  });

  const [runQueryUsersByIds] = useLazyQuery(GET_USERS_BY_IDS, {
    fetchPolicy: "network-only",
  });

  const [runQueryInstituteUsers] = useLazyQuery(
    GET_USERS_BY_INSTITUTE_PAGINATED,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryInstituteUsersByDistricts] = useLazyQuery(
    GET_USERS_BY_INSTITUTE_AND_DISTRICTS_PAGINATED,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryDistrictUsers] = useLazyQuery(
    GET_USERS_BY_DISTRICT_PAGINATED,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryScormAttempts] = useLazyQuery(
    GET_SCORM_QUIZ_ATTEMPT_WITH_FILTER,
    {
      fetchPolicy: "network-only",
    },
  );

  const [runQueryLatestQualifications] = useLazyQuery(
    GET_LATEST_QUALIFICATIONS_BY_USER_IDS,
    { fetchPolicy: "network-only" },
  );

  const scormMappingIds = useMemo(
    () => asdmCourseReportConfig.mappingId || [],
    [],
  );

  const allProjectIds = useMemo(
    () => Object.values(asdmCourseReportConfig.asdmProjectId || {}).filter(Boolean),
    [],
  );

  const normalizeProjectIds = useCallback(
    (value) => {
      const list = Array.isArray(value) ? value : value ? [value] : [];
      if (list.length) return list;
      if (allProjectIds.length) return allProjectIds;
      if (asdmCourseReportConfig.projectId) return [asdmCourseReportConfig.projectId];
      return [];
    },
    [allProjectIds],
  );

  const buildScormAttemptFilter = useCallback(
    (userIds = []) => {
      const filter = { user_id: { _in: userIds } };
      if (scormMappingIds.length) {
        filter.mapping_id = { _in: scormMappingIds };
      }
      return filter;
    },
    [scormMappingIds],
  );

  const fetchScormAttemptsByUsers = useCallback(
    async (userIds = []) => {
      if (!userIds.length) return [];
      const chunks = chunkArray(userIds, 500);
      let scormAttempts = [];
      for (const c of chunks) {
        const { data } = await runQueryScormAttempts({
          variables: {
            scormQuizAttemptSearchExp: buildScormAttemptFilter(c),
          },
        });
        scormAttempts.push(
          ...(data?.courses_course_scorm_quiz_attempt_data || []),
        );
      }
      return scormAttempts;
    },
    [buildScormAttemptFilter, runQueryScormAttempts],
  );

  const userTableColumns = useMemo(
    () => [
      { name: "Name", selector: (row) => row.name || "N/A", wrap: true },
      {
        name: "Mobile Number",
        selector: (row) => row.mobile_number || "N/A",
        wrap: true,
      },
      { name: "Email", selector: (row) => row.email || "N/A", wrap: true },
      {
        name: "Registration Date",
        selector: (row) => row.registrationDate || "N/A",
        wrap: true,
      },
      {
        name: "Certification Date",
        selector: (row) => row.certificationDate || "N/A",
        wrap: true,
      },
      {
        name: "Certificate",
        cell: (row) =>
          row.certUrl ? (
            <a
              href={row.certUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline"
            >
              View PDF
            </a>
          ) : (
            "—"
          ),
        center: true,
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 1 Marks</div>
        ),
        selector: (row) => row.g1m || "N/A",
        width: "160px",
      },

      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 1 Interpretation"
          </div>
        ),
        selector: (row) => row.g1_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 2 Marks</div>
        ),
        selector: (row) => row.g2m || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 2 Interpretation
          </div>
        ),
        selector: (row) => row.g2_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 3 Marks</div>
        ),
        selector: (row) => row.g3m || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 3 Interpretation
          </div>
        ),
        selector: (row) => row.g3_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Career Group 4 Marks</div>
        ),
        selector: (row) => row.g4m || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>
            Career Group 4 Interpretation
          </div>
        ),
        selector: (row) => row.g4_word || "N/A",
        width: "160px",
      },
      {
        name: (
          <div style={{ wordBreak: "break-word" }}>Digital Literacy Score</div>
        ),
        selector: (row) => row.digital_marks || "N/A",
        width: "160px",
      },
    ],
    [],
  );

  const userTableStyles = useMemo(
    () => ({
      headCells: { style: { fontWeight: 600, fontSize: "13px" } },
      cells: {
        style: { fontSize: "13px", paddingTop: "8px", paddingBottom: "8px" },
      },
    }),
    [],
  );

  const fetchLatestQualifications = useCallback(
    async (userIds = []) => {
      if (!userIds.length) return {};
      const map = {};
      const chunks = chunkArray(userIds, 1000);
      for (const c of chunks) {
        const { data } = await runQueryLatestQualifications({
          variables: { user_ids: c },
        });
        (data?.courses_user_academic_qualifications || []).forEach((row) => {
          if (row?.qualifications_lookup?.name) {
            map[row.user_id] = row.qualifications_lookup.name;
          }
        });
      }
      return map;
    },
    [runQueryLatestQualifications],
  );

  const filterStats = async (filter) => {
    try {
      setLoadingPercent(0);
      setExpandedGroup(null);
      setBatchTable({ totalRows: 0, pageIndex: 0, pageSize: 25, rows: [] });
      setSelectedDistricts([]);
      setSelectedInstitutes([]);
      setSelectedClasses([]);
      setSelectedGenders([]);
      setSummaryRows([]);
      setCareerGroupSummary(createCareerGroupTemplate());
      setCareerGroupLoading(false);
      setDigitalQuestionSummary(createDigitalQuestionTemplate());
      setDigitalQuestionLoading(false);
      setDigitalQuestionError("");
      careerGroupCacheRef.current.clear();

      const startDate = filter?.start_date?.split("T")[0] || "2024-04-01";
      const endDate =
        filter?.end_date?.split("T")[0] ||
        new Date().toISOString().split("T")[0];

      const projectIds = normalizeProjectIds(filter.project_id);

      lastFilterRef.current = {
        startDate,
        endDate,
        project_ids: projectIds,
        course_ids: [178, 205],
      };

      const nextSummaryRows = [];

      const pageSize = 2000;
      let offset = 0;

      setLoadingPercent(5);

      while (true) {
        const { data } = await runQuerySummaryIds({
          variables: {
            startDate,
            endDate,
            course_ids: [178, 205],
            project_ids: projectIds,
            limit: pageSize,
            offset,
          },
        });

        const users = data?.courses_users || [];
        if (!users.length) break;

        const userIds = users.map((u) => u.id).filter(Boolean);
        const qualificationMap = await fetchLatestQualifications(userIds);

        users.forEach((u) => {
          const inst = getInstituteLabel(_.get(u, "batch_enrollments.0"));
          const dist = getDistrictLabel(_.get(u, "batch_enrollments.0"));
          const classLabel = normalizeClass(qualificationMap[u.id]);
          const genderLabel = normalizeGender(u.gender);

          nextSummaryRows.push({
            user_id: u.id,
            institute: inst,
            district: dist,
            class_label: classLabel,
            gender_label: genderLabel,
          });
        });

        offset += pageSize;

        setLoadingPercent((p) => Math.min(45, p + 5));

        await new Promise((r) => setTimeout(r, 0));
      }

      setSummaryRows(nextSummaryRows);

      setLoadingPercent(100);

      try {
        client.cache.evict({ fieldName: "courses_users" });
        client.cache.gc();
      } catch (_) {}
    } catch (e) {
      console.error("Error while fetching summary:", e);
      setLoadingPercent(0);
    }
  };

  const loadInstitutePage = useCallback(
    async ({ instituteName, pageIndex, pageSize }) => {
      const base = lastFilterRef.current;
      if (!base) return;

      setScormLoading(true);
      setLoadingPercent(10);

      const offset = pageIndex * pageSize;

      const hasDistrictFilter = selectedDistricts.length > 0;
      const { data } = await (
        hasDistrictFilter
          ? runQueryInstituteUsersByDistricts
          : runQueryInstituteUsers
      )({
        variables: {
          ...base,
          institute_name: instituteName,
          limit: pageSize,
          offset,
          ...(hasDistrictFilter ? { districts: selectedDistricts } : {}),
        },
      });

      const totalRows = data?.courses_users_aggregate?.aggregate?.count || 0;
      const users = data?.courses_users || [];
      setLoadingPercent(35);

      const userIds = users.map((u) => u.id).filter(Boolean);
      const qualificationMap = await fetchLatestQualifications(userIds);

      let usersWithScorm = users;

      if (userIds.length) {
        const scormAttempts = await fetchScormAttemptsByUsers(userIds);

        const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

        usersWithScorm = users.map((u) => {
          const attempts = attemptsByUserId[u.id] || [];
          return { ...u, scorm_scores: calculateScormScores(attempts) };
        });
      }

      setLoadingPercent(85);

      const normalizedRows = usersWithScorm.map((user) => {
        const certUrl = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.certificate.certificate_pdf_url",
        );
        const registrationDate = _.get(user, "batch_enrollments.0.updated_at");
        const certificationDate = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.created_at",
        );

        const sc = user.scorm_scores || {};
        const genderLabel = normalizeGender(user.gender);
        const classLabel = normalizeClass(qualificationMap[user.id]);
        return {
          ...user,
          certUrl,
          registrationDate,
          certificationDate,
          g1m: sc.g1m,
          g1_word: sc.g1_word,
          g2m: sc.g2m,
          g2_word: sc.g2_word,
          g3m: sc.g3m,
          g3_word: sc.g3_word,
          g4m: sc.g4m,
          g4_word: sc.g4_word,
          digital_marks: sc.digital_marks,
          gender_label: genderLabel,
          class_label: classLabel,
        };
      });

      setBatchTable({
        totalRows,
        pageIndex,
        pageSize,
        rows: normalizedRows,
      });

      setLoadingPercent(100);
      setScormLoading(false);

      try {
        client.cache.evict({
          fieldName: "courses_course_scorm_quiz_attempt_data",
        });
        client.cache.evict({ fieldName: "courses_course_module_user_attempt" });
        client.cache.gc();
      } catch (_) {}
    },
    [
      runQueryInstituteUsers,
      runQueryInstituteUsersByDistricts,
      fetchScormAttemptsByUsers,
      client,
      fetchLatestQualifications,
      selectedDistricts,
    ],
  );

  const loadDistrictPage = useCallback(
    async ({ districtName, pageIndex, pageSize }) => {
      const base = lastFilterRef.current;
      if (!base) return;

      setScormLoading(true);
      setLoadingPercent(10);

      const offset = pageIndex * pageSize;

      const { data } = await runQueryDistrictUsers({
        variables: {
          ...base,
          district: districtName,
          limit: pageSize,
          offset,
        },
      });

      const totalRows = data?.courses_users_aggregate?.aggregate?.count || 0;
      const users = data?.courses_users || [];
      setLoadingPercent(35);

      const userIds = users.map((u) => u.id).filter(Boolean);
      const qualificationMap = await fetchLatestQualifications(userIds);

      let usersWithScorm = users;

      if (userIds.length) {
        const scormAttempts = await fetchScormAttemptsByUsers(userIds);

        const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

        usersWithScorm = users.map((u) => {
          console.log("uuu", u);
          console.log("attemptsByUserId", attemptsByUserId);

          const attempts = attemptsByUserId[u.id] || [];
          return { ...u, scorm_scores: calculateScormScores(attempts) };
        });
      }

      setLoadingPercent(85);

      const normalizedRows = usersWithScorm.map((user) => {
        const certUrl = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.certificate.certificate_pdf_url",
        );
        const registrationDate = _.get(user, "batch_enrollments.0.updated_at");
        const certificationDate = _.get(
          user,
          "batch_enrollments.0.course_enrolment.course_complete_obj.created_at",
        );

        const sc = user.scorm_scores || {};
        const genderLabel = normalizeGender(user.gender);
        const classLabel = normalizeClass(qualificationMap[user.id]);
        return {
          ...user,
          certUrl,
          registrationDate,
          certificationDate,
          g1m: sc.g1m,
          g1_word: sc.g1_word,
          g2m: sc.g2m,
          g2_word: sc.g2_word,
          g3m: sc.g3m,
          g3_word: sc.g3_word,
          g4m: sc.g4m,
          g4_word: sc.g4_word,
          digital_marks: sc.digital_marks,
          gender_label: genderLabel,
          class_label: classLabel,
        };
      });

      setBatchTable({
        totalRows,
        pageIndex,
        pageSize,
        rows: normalizedRows,
      });

      setLoadingPercent(100);
      setScormLoading(false);

      try {
        client.cache.evict({
          fieldName: "courses_course_scorm_quiz_attempt_data",
        });
        client.cache.evict({ fieldName: "courses_course_module_user_attempt" });
        client.cache.gc();
      } catch (_) {}
    },
    [
      runQueryDistrictUsers,
      fetchScormAttemptsByUsers,
      client,
      fetchLatestQualifications,
    ],
  );

  const groupMode =
    selectedInstitutes.length > 0
      ? "institute"
      : selectedDistricts.length > 0
        ? "district"
        : "institute";

  const onToggleGroup = async (groupName) => {
    const next = expandedGroup === groupName ? null : groupName;
    setExpandedGroup(next);

    if (!next) return;

    const pageSize = batchTable.pageSize || 25;
    if (groupMode === "district") {
      await loadDistrictPage({
        districtName: next,
        pageIndex: 0,
        pageSize,
      });
      return;
    }

    await loadInstitutePage({
      instituteName: next,
      pageIndex: 0,
      pageSize,
    });
  };

  const downloadExcelForFilters = async () => {
    const base = lastFilterRef.current;
    if (!base) return;

    const pageSize = 500;
    if (!filteredSummaryRows.length) return;

    const exportUserIds = Array.from(
      new Set(filteredSummaryRows.map((row) => row.user_id).filter(Boolean)),
    );
    if (!exportUserIds.length) return;

    const summaryByUserId = new Map(
      filteredSummaryRows.map((row) => [row.user_id, row]),
    );

    const ws = XLSX.utils.aoa_to_sheet([
      [
        "S. No.",
        "Name",
        "Mobile Number",
        "Email",
        "Gender",
        "Qualification",
        "Institute Name",
        "District",
        "Registration Date",
        "Certification Date",
        "Certificate URL",
        "Career Group 1 Marks",
        "Career Group 1 Interpretation",
        "Career Group 2 Marks",
        "Career Group 2 Interpretation",
        "Career Group 3 Marks",
        "Career Group 3 Interpretation",
        "Career Group 4 Marks",
        "Career Group 4 Interpretation",
        "Digital Literacy Score",
      ],
    ]);

    let serial = 1;
    setScormLoading(true);
    setLoadingPercent(5);

    const userChunks = chunkArray(exportUserIds, pageSize);
    let processed = 0;

    for (const chunk of userChunks) {
      const { data } = await runQueryUsersByIds({
        variables: { user_ids: chunk, project_ids: base.project_ids },
      });

      const users = data?.courses_users || [];
      if (!users.length) {
        processed += chunk.length;
        continue;
      }

      const qualificationMap = await fetchLatestQualifications(chunk);

      const scormAttempts = await fetchScormAttemptsByUsers(chunk);

      const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

      const rows = users
        .map((u) => {
          const summary = summaryByUserId.get(u.id);
          if (!summary) return null;

          const enrollments = u.batch_enrollments || [];
          const matchedEnrollment =
            enrollments.find(
              (enrollment) =>
                getInstituteLabel(enrollment) === summary.institute &&
                getDistrictLabel(enrollment) === summary.district,
            ) || enrollments[0];

          const certUrl = _.get(
            matchedEnrollment,
            "course_enrolment.course_complete_obj.certificate.certificate_pdf_url",
          );
          const registrationDate = matchedEnrollment?.updated_at || "N/A";
          const certificationDate =
            matchedEnrollment?.course_enrolment?.course_complete_obj
              ?.created_at || "N/A";

          const scores = calculateScormScores(attemptsByUserId[u.id] || []);
          const genderLabel = summary.gender_label || normalizeGender(u.gender);
          const classLabel =
            summary.class_label || normalizeClass(qualificationMap[u.id]);

          return [
            serial++,
            u.name || "N/A",
            u.mobile_number || "N/A",
            u.email || "N/A",
            genderLabel || "N/A",
            classLabel || NO_CLASS_LABEL,
            summary.institute || NO_INSTITUTE_LABEL,
            summary.district || NO_DISTRICT_LABEL,
            registrationDate,
            certificationDate,
            certUrl || "—",
            scores.g1m || "N/A",
            scores.g1_word || "N/A",
            scores.g2m || "N/A",
            scores.g2_word || "N/A",
            scores.g3m || "N/A",
            scores.g3_word || "N/A",
            scores.g4m || "N/A",
            scores.g4_word || "N/A",
            scores.digital_marks || "N/A",
          ];
        })
        .filter(Boolean);

      XLSX.utils.sheet_add_aoa(ws, rows, { origin: -1 });

      processed += chunk.length;
      setLoadingPercent(
        5 + Math.round((processed / exportUserIds.length) * 90),
      );
      await new Promise((r) => setTimeout(r, 0));
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ASDM Report");

    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `ASDM_Filtered_Report.xlsx`,
    );

    setLoadingPercent(100);
    setScormLoading(false);
  };

  const filteredSummaryRows = useMemo(
    () =>
      filterSummaryRows(summaryRows, {
        selectedDistricts,
        selectedInstitutes,
        selectedClasses,
        selectedGenders,
      }),
    [
      summaryRows,
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
    ],
  );

  const instituteEntries = useMemo(() => {
    const totals = filteredSummaryRows.reduce((acc, row) => {
      acc[row.institute] = (acc[row.institute] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(totals).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  }, [filteredSummaryRows]);

  const districtEntries = useMemo(() => {
    const totals = filteredSummaryRows.reduce((acc, row) => {
      acc[row.district] = (acc[row.district] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(totals).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  }, [filteredSummaryRows]);

  const groupEntries = useMemo(
    () => (groupMode === "district" ? districtEntries : instituteEntries),
    [groupMode, districtEntries, instituteEntries],
  );

  const groupHeading = useMemo(
    () =>
      groupMode === "district" ? "Users by District" : "Users by Institute",
    [groupMode],
  );

  const districtOptions = useMemo(() => {
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
      ignore: { district: true },
    });
    const districts = Array.from(new Set(rows.map((row) => row.district)));
    return districts
      .sort((a, b) => a.localeCompare(b))
      .map((district) => ({ value: district, label: district }));
  }, [summaryRows, selectedInstitutes, selectedClasses, selectedGenders]);

  const instituteOptions = useMemo(() => {
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
      ignore: { institute: true },
    });
    const institutes = Array.from(new Set(rows.map((row) => row.institute)));
    return institutes
      .sort((a, b) => a.localeCompare(b))
      .map((inst) => ({ value: inst, label: inst }));
  }, [summaryRows, selectedDistricts, selectedClasses, selectedGenders]);

  const classOptions = useMemo(() => {
    const rows = filterSummaryRows(summaryRows, {
      selectedDistricts,
      selectedInstitutes,
      selectedClasses,
      selectedGenders,
      ignore: { class: true },
    });
    const classes = Array.from(new Set(rows.map((row) => row.class_label)));
    return classes
      .sort((a, b) => a.localeCompare(b))
      .map((classLabel) => ({ value: classLabel, label: classLabel }));
  }, [summaryRows, selectedDistricts, selectedInstitutes, selectedGenders]);

  const hasRowFilters = selectedClasses.length || selectedGenders.length;

  const filteredBatchRows = useMemo(
    () =>
      hasRowFilters
        ? batchTable.rows.filter((row) => {
            const matchesGender = matchesMultiSelect(
              row.gender_label,
              selectedGenders,
            );
            if (!matchesGender) return false;
            const matchesClass = matchesMultiSelect(
              row.class_label,
              selectedClasses,
            );
            if (!matchesClass) return false;
            return true;
          })
        : batchTable.rows,
    [batchTable.rows, hasRowFilters, selectedClasses, selectedGenders],
  );

  const hasCareerGroupUsers = useMemo(() => {
    if (!expandedGroup) return false;
    return filteredSummaryRows.some((row) =>
      groupMode === "district"
        ? row.district === expandedGroup
        : row.institute === expandedGroup,
    );
  }, [expandedGroup, filteredSummaryRows, groupMode]);

  const buildCareerGroupCounts = useCallback(
    async (userIds = []) => {
      const template = createCareerGroupTemplate();
      if (!userIds.length) return template;

      const summaryMap = new Map(template.map((entry) => [entry.key, entry]));
      const chunks = chunkArray(userIds, 500);

      for (const userChunk of chunks) {
        const scormAttempts = await fetchScormAttemptsByUsers(userChunk);

        const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

        userChunk.forEach((userId) => {
          const scores = calculateScormScores(attemptsByUserId[userId] || []);
          CAREER_GROUPS.forEach((group) => {
            const interpretation = scores[group.wordKey];
            if (!INTERPRETATION_LABELS.includes(interpretation)) return;
            summaryMap.get(group.key).counts[interpretation] += 1;
          });
        });

        await new Promise((r) => setTimeout(r, 0));
      }

      return template;
    },
    [fetchScormAttemptsByUsers],
  );

  const buildDigitalQuestionSummary = useCallback(
    async (userIds = []) => {
      const template = createDigitalQuestionTemplate();
      if (!userIds.length) return template;

      const summaryMap = new Map(
        template.map((entry) => [entry.question_id, entry]),
      );
      const chunks = chunkArray(userIds, 500);
      let expectedTotal = 0;

      for (const userChunk of chunks) {
        const scormAttempts = await fetchScormAttemptsByUsers(userChunk);
        const attemptsByUserId = _.groupBy(scormAttempts, "user_id");

        userChunk.forEach((userId) => {
          const attempts = attemptsByUserId[userId] || [];
          if (!attempts.length) return;
          expectedTotal += 1;
          const latestResponses = getLatestAttemptResponses(attempts);

          latestResponses.forEach((res) => {
            const responseId = res.question_id || res.slide_id || "";
            const summaryEntry = summaryMap.get(responseId);
            if (!summaryEntry) return;

            const trimmed = `${res.user_response || ""}`.trim();
            if (!trimmed) return;
            const firstChar = trimmed[0]?.toUpperCase();
            const answer =
              firstChar === "A" ? "Yes" : firstChar === "B" ? "No" : null;
            if (!answer) return;

            summaryEntry.counts[answer] += 1;
          });
        });

        await new Promise((r) => setTimeout(r, 0));
      }

      template.forEach((entry) => {
        const total = (entry.counts.Yes || 0) + (entry.counts.No || 0);
        if (total !== expectedTotal) {
          throw new Error(
            `Digital literacy data integrity error for "${entry.question}": expected ${expectedTotal}, got ${total}.`,
          );
        }
      });

      return template;
    },
    [fetchScormAttemptsByUsers],
  );

  const careerGroupKey = useMemo(() => {
    if (!expandedGroup) return "";
    return JSON.stringify({
      groupMode,
      groupName: expandedGroup,
      districts: [...selectedDistricts].sort(),
      institutes: [...selectedInstitutes].sort(),
      classes: [...selectedClasses].sort(),
      genders: [...selectedGenders].sort(),
    });
  }, [
    expandedGroup,
    groupMode,
    selectedDistricts,
    selectedInstitutes,
    selectedClasses,
    selectedGenders,
  ]);

  useEffect(() => {
    if (!expandedGroup) {
      setCareerGroupSummary(createCareerGroupTemplate());
      setCareerGroupLoading(false);
      return;
    }

    const base = lastFilterRef.current;
    if (!base) return;

    const groupRows = filteredSummaryRows.filter((row) =>
      groupMode === "district"
        ? row.district === expandedGroup
        : row.institute === expandedGroup,
    );

    const userIds = Array.from(
      new Set(groupRows.map((row) => row.user_id).filter(Boolean)),
    );

    if (!userIds.length) {
      setCareerGroupSummary(createCareerGroupTemplate());
      setCareerGroupLoading(false);
      return;
    }

    if (careerGroupCacheRef.current.has(careerGroupKey)) {
      setCareerGroupSummary(careerGroupCacheRef.current.get(careerGroupKey));
      setCareerGroupLoading(false);
      return;
    }

    const requestId = careerGroupRequestIdRef.current + 1;
    careerGroupRequestIdRef.current = requestId;
    setCareerGroupLoading(true);
    setCareerGroupSummary(createCareerGroupTemplate());

    buildCareerGroupCounts(userIds)
      .then((summary) => {
        if (careerGroupRequestIdRef.current !== requestId) return;
        careerGroupCacheRef.current.set(careerGroupKey, summary);
        setCareerGroupSummary(summary);
        setCareerGroupLoading(false);
      })
      .catch(() => {
        if (careerGroupRequestIdRef.current !== requestId) return;
        setCareerGroupLoading(false);
      });
  }, [
    buildCareerGroupCounts,
    careerGroupKey,
    expandedGroup,
    filteredSummaryRows,
    groupMode,
  ]);

  useEffect(() => {
    if (!filteredSummaryRows.length) {
      setDigitalQuestionSummary(createDigitalQuestionTemplate());
      setDigitalQuestionLoading(false);
      setDigitalQuestionError("");
      return;
    }

    const userIds = Array.from(
      new Set(filteredSummaryRows.map((row) => row.user_id).filter(Boolean)),
    );

    if (!userIds.length) {
      setDigitalQuestionSummary(createDigitalQuestionTemplate());
      setDigitalQuestionLoading(false);
      setDigitalQuestionError("");
      return;
    }

    const requestId = digitalQuestionRequestIdRef.current + 1;
    digitalQuestionRequestIdRef.current = requestId;
    setDigitalQuestionLoading(true);
    setDigitalQuestionError("");
    setDigitalQuestionSummary(createDigitalQuestionTemplate());

    buildDigitalQuestionSummary(userIds)
      .then((summary) => {
        if (digitalQuestionRequestIdRef.current !== requestId) return;
        setDigitalQuestionSummary(summary);
        setDigitalQuestionLoading(false);
      })
      .catch((err) => {
        if (digitalQuestionRequestIdRef.current !== requestId) return;
        setDigitalQuestionError(err?.message || "Data integrity error.");
        setDigitalQuestionLoading(false);
      });
  }, [buildDigitalQuestionSummary, filteredSummaryRows]);

  const selectedInsightCount = useMemo(() => {
    if (!selectedCareerGroup || !selectedInterpretation) return null;
    const entry = careerGroupSummary.find(
      (item) => item.key === selectedCareerGroup,
    );
    if (!entry) return null;
    return entry.counts[selectedInterpretation] ?? 0;
  }, [careerGroupSummary, selectedCareerGroup, selectedInterpretation]);

  const selectedCareerGroupLabel = useMemo(() => {
    const entry = CAREER_GROUPS.find(
      (group) => group.key === selectedCareerGroup,
    );
    return entry?.label || "";
  }, [selectedCareerGroup]);

  const careerGroupOptions = useMemo(
    () =>
      CAREER_GROUPS.map((group) => ({ value: group.key, label: group.label })),
    [],
  );

  const interpretationOptions = useMemo(
    () => INTERPRETATION_LABELS.map((label) => ({ value: label, label })),
    [],
  );

  const hasActiveFilters = Boolean(
    selectedDistricts.length ||
    selectedInstitutes.length ||
    selectedClasses.length ||
    selectedGenders.length,
  );

  useEffect(() => {
    if (!selectedInstitutes.length) return;
    const available = new Set(instituteOptions.map((opt) => opt.value));
    const nextSelected = selectedInstitutes.filter((inst) =>
      available.has(inst),
    );
    if (nextSelected.length !== selectedInstitutes.length) {
      setSelectedInstitutes(nextSelected);
    }
  }, [instituteOptions, selectedInstitutes]);

  useEffect(() => {
    if (!expandedGroup) return;
    const isVisible = groupEntries.some(
      ([groupName]) => groupName === expandedGroup,
    );
    if (!isVisible) {
      setExpandedGroup(null);
      setBatchTable((prev) => ({
        ...prev,
        totalRows: 0,
        pageIndex: 0,
        rows: [],
      }));
    }
  }, [expandedGroup, groupEntries]);

  return (
    <Container>
      <div className="mb-4">
        <h1 className="text-base font-bold text-slate-900">Data Explorer</h1>
        <p className="mt-1 text-sm text-slate-600">
          Select date range and phase, then click{" "}
          <span className="font-medium text-slate-800">Filter</span> to fetch
          data.
        </p>
      </div>
      <Form
        formBuilder={ASDMStatsFormBuilder}
        className="mt-2 grid grid-cols-4 gap-x-6 mx-2"
        submitButton={{
          text: "Filter",
          className:
            "btn-primary bg-orange text-sm font-semibold text-white rounded-md px-6",
        }}
        onSubmit={(filter) => filterStats(filter)}
      />
      {loadingPercent > 0 && loadingPercent < 100 && (
        <div className="mt-4">
          <b> Loading... {loadingPercent}% </b>
        </div>
      )}
      {!!summaryRows.length && (
        <div className="mx-auto mt-6">
          <div className="bg-white p-6 rounded-md shadow-md">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Refine Results</h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedDistricts([]);
                  setSelectedInstitutes([]);
                  setSelectedClasses([]);
                  setSelectedGenders([]);
                  setSelectedCareerGroup("");
                  setSelectedInterpretation("");
                  setExpandedGroup(null);
                }}
                disabled={!hasActiveFilters}
                className="text-sm text-blue-600 hover:underline disabled:opacity-50"
              >
                Reset Filters
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <MultiSelectDropdown
                label="Filter by District"
                options={districtOptions}
                valueField="value"
                displayField="label"
                selectedValue={selectedDistricts}
                setSelectedValue={setSelectedDistricts}
              />
              <MultiSelectDropdown
                label="Filter by Institute"
                options={instituteOptions}
                valueField="value"
                displayField="label"
                selectedValue={selectedInstitutes}
                setSelectedValue={setSelectedInstitutes}
              />
              <MultiSelectDropdown
                label="Filter by Class"
                options={classOptions}
                valueField="value"
                displayField="label"
                selectedValue={selectedClasses}
                setSelectedValue={setSelectedClasses}
              />
              <MultiSelectDropdown
                label="Filter by Gender"
                options={GENDER_OPTIONS}
                valueField="value"
                displayField="label"
                selectedValue={selectedGenders}
                setSelectedValue={setSelectedGenders}
              />
            </div>
          </div>
        </div>
      )}
      {!!summaryRows.length && (
        <div className="mx-auto mt-6">
          <div className="bg-white p-6 rounded-md shadow-md">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                Digital Literacy Response Summary
              </h3>
              {digitalQuestionLoading && (
                <span className="text-xs text-gray-500">
                  Calculating responses...
                </span>
              )}
            </div>
            {digitalQuestionError && (
              <p className="mt-2 text-sm text-red-600">
                {digitalQuestionError}
              </p>
            )}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {digitalQuestionSummary.map((entry) => {
                const yesCount = entry.counts.Yes || 0;
                const noCount = entry.counts.No || 0;
                const total = yesCount + noCount;
                const barData = [
                  { name: "Yes", value: yesCount },
                  { name: "No", value: noCount },
                ];
                return (
                  <div
                    key={entry.question_id}
                    className="border rounded-md p-4 bg-gray-50"
                  >
                    <div className="text-sm font-semibold">
                      Question {entry.question_index}
                    </div>
                    <div className="text-xs text-gray-600">
                      {entry.question}
                    </div>
                    <div className="mt-3 h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barData} margin={{ top: 8, right: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {barData.map((item, index) => (
                              <Cell
                                key={`${entry.question_id}-${index}`}
                                fill={
                                  item.name === "Yes" ? "#22c55e" : "#ef4444"
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 text-xs text-gray-700">
                      Yes: {yesCount} · No: {noCount} · Total: {total}
                    </div>
                  </div>
                );
              })}
              {!digitalQuestionSummary.length && (
                <p className="text-sm text-gray-600">
                  No digital literacy data available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
